import React, { useEffect } from 'react';

import { Stack, Typography } from '@mui/material';
import { useUnit } from 'effector-react';

import { StrategyParams } from './StrategyParams';
import { StrategiesParams$, strategiesParamsApi } from '../../effector/strategiesParams';
import { StrategySummary } from '../../types/strategySummary';
import { formatMoney, formatPercent } from '../../utils';

export type OptionFormatter = (params: StrategySummary, accessor: keyof StrategySummary) => React.ReactNode;

export type Option = {
  name: string;
  accessor: keyof StrategySummary;
  formatter?: OptionFormatter;
};

const textModifier: OptionFormatter = (params, accessor) => {
  const value = params[accessor];

  if (typeof value === 'boolean') {
    return value ? 'Да' : 'Нет';
  }

  return String(value ?? '');
};

const moneyModifier: OptionFormatter = (params, accessor) => formatMoney(params[accessor] as number, params.currency);

const percentModifier: OptionFormatter = (params, accessor) => {
  const value = params[accessor];
  return typeof value === 'number' ? formatPercent(value, 0) : '';
};

const tariffModifier: OptionFormatter = (params, accessor) => {
  const tariffs = params[accessor] as StrategySummary['availableTariffs'];
  return (
    <Stack spacing={0.5}>
      {tariffs.map((item, ind) => (
        <Stack key={ind} direction="row" spacing={1}>
          <Typography variant="body2">{item.clientTariff ?? 'все остальные'}</Typography>
          <Typography variant="body2" color="text.secondary">
            AC: {item.acTariff}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AК: {item.afTariff}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const options: Option[] = [
  { name: 'Минимальная сумма', accessor: 'subscriptionThreshold', formatter: moneyModifier },
  { name: 'Автоконсультирование', accessor: 'autoconsult', formatter: textModifier },
  { name: 'Автоследование', accessor: 'autofollow', formatter: textModifier },
  { name: 'Категория', accessor: 'category' },
  { name: 'Срок инвестиций', accessor: 'duration' },
  { name: 'Минимальный инвестиционный профиль', accessor: 'minInvestProfile' },
  { name: 'Доходность', accessor: 'estimatedProfit', formatter: percentModifier },
  { name: 'Просадка', accessor: 'estimatedDrawdown', formatter: percentModifier },
  { name: 'Квалифицированный инвестор', accessor: 'forQualifiedInvestorsOnly', formatter: textModifier },
  { name: 'Скрыть портфель', accessor: 'hidePortfolio', formatter: textModifier },
  { name: 'Скрыть последние сигналы', accessor: 'hideRecentSignals' as keyof StrategySummary, formatter: textModifier },
  { name: 'ИИС', accessor: 'iis', formatter: textModifier },
  { name: 'Бенчмарк', accessor: 'index' },
  { name: 'Цена Автоследования', accessor: 'priceAF' },
  { name: 'Цена Автоконсультирования', accessor: 'priceAC' },
  { name: 'Показать портфель полностью', accessor: 'showFullPortfolio', formatter: textModifier },
  { name: 'Тестовый режим', accessor: 'testMode', formatter: textModifier },
  { name: 'Закрытая', accessor: 'isRestricted', formatter: textModifier },
  { name: 'Доступные тарифы', accessor: 'availableTariffs', formatter: tariffModifier },
];

export const StrategiesParams: React.FC = () => {
  const params = useUnit(StrategiesParams$);

  useEffect(() => {
    strategiesParamsApi.get('');
  }, []);

  if (!params) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {params.map((item, index) => (
        <StrategyParams key={index} strategy={item} options={options} />
      ))}
    </Stack>
  );
};
